// Thêm nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): SĐT đã có tài khoản ShopTrack → gắn vào tài khoản đó (người đó dùng
// mật khẩu của chính họ, không đổi gì của họ); SĐT mới → tạo tài khoản với mật khẩu tạm chủ đặt, nhân viên phải đổi ở
// lần đăng nhập đầu. Xong hiện khung "gửi cho nhân viên" để chủ chép SĐT + mật khẩu tạm.
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { get, post } from '@/lib/api'
import { getPhoneValidationError } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { browserRandom, makeTempPassword, type UnitRoleRow } from './types'
import { CredentialsBox } from './CredentialsBox'

type Lookup = { exists: boolean; member: boolean } | null

export function AddMemberDialog({ open, onOpenChange, roles, passwordMin }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  roles: UnitRoleRow[]
  passwordMin: number
}) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [roleId, setRoleId] = useState('')
  const [password, setPassword] = useState('')
  const [lookup, setLookup] = useState<Lookup>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ name: string; phone: string; password: string | null } | null>(null)

  // Chỉ đặt lại khi MỞ hộp — danh sách tải lại sau khi thêm (roles đổi) không được xoá khung mật khẩu tạm vừa hiện
  useEffect(() => {
    if (!open) return
    setName(''); setPhone(null); setEmail(''); setPassword(makeTempPassword(browserRandom)); setLookup(null); setError(null); setDone(null)
    setRoleId(roles.length === 1 ? roles[0]!.id : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // SĐT đủ số → hỏi máy chủ đã có tài khoản chưa (có rồi thì không cần mật khẩu tạm)
  useEffect(() => {
    setLookup(null)
    if (!phone || getPhoneValidationError(phone, true)) return
    let alive = true
    const t = setTimeout(() => {
      get<{ exists: boolean; member: boolean }>(`/settings/members/lookup?phone=${encodeURIComponent(phone)}`)
        .then((r) => { if (alive) setLookup(r) })
        .catch(() => {})
    }, 300)
    return () => { alive = false; clearTimeout(t) }
  }, [phone])

  const save = useMutation({
    mutationFn: () =>
      post<{ member: { userId: string; name: string | null }; existingAccount: boolean }>('/settings/members', {
        name: name.trim(),
        phone,
        email: email.trim() || undefined,
        roleId,
        ...(lookup?.exists ? {} : { password }),
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['unit-members'] })
      toast.success(r.existingAccount ? 'Đã thêm nhân viên (tài khoản sẵn có)' : 'Đã tạo tài khoản nhân viên')
      setDone({ name: name.trim(), phone: phone ?? '', password: r.existingAccount ? null : password })
    },
    onError: (e) => setError((e as Error).message),
  })

  function submit() {
    setError(null)
    if (!name.trim()) return setError('Nhập họ tên nhân viên')
    const phoneErr = getPhoneValidationError(phone, true)
    if (phoneErr) return setError(phoneErr)
    if (lookup?.member) return setError('Người này đã có trong đơn vị')
    if (!roleId) return setError('Chọn vai trò')
    if (!lookup?.exists && password.length < passwordMin) return setError(`Mật khẩu tạm tối thiểu ${passwordMin} ký tự`)
    save.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!save.isPending) onOpenChange(v) }}>
      <DialogContent className="sm:max-w-xl max-h-[92dvh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{done ? 'Đã thêm nhân viên' : 'Thêm nhân viên'}</DialogTitle>
        </DialogHeader>

        {done ? (
          <CredentialsBox name={done.name} phone={done.phone} password={done.password} />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1.5 sm:col-span-2">
              <span className="text-sm text-inkSoft">Họ tên *</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder="VD: Nguyễn Thị Lan" className="h-11 desktop:h-9" disabled={save.isPending} />
            </label>
            <div className="space-y-1.5">
              <span className="block text-sm text-inkSoft">Số điện thoại đăng nhập *</span>
              <PhoneInput value={phone} onValueChange={setPhone} showValidation disabled={save.isPending} />
            </div>
            <label className="block space-y-1.5">
              <span className="text-sm text-inkSoft">Email</span>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="lan@vidu.vn" className="h-11 desktop:h-9" disabled={save.isPending || lookup?.exists === true} />
            </label>
            <div className="space-y-1.5 sm:col-span-2">
              <span className="block text-sm text-inkSoft">Vai trò *</span>
              <Select value={roleId} onValueChange={setRoleId} disabled={save.isPending}>
                <SelectTrigger aria-label="Vai trò"><SelectValue placeholder="Chọn vai trò" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {lookup?.member ? (
              <p className="rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink sm:col-span-2">Người này đã có trong đơn vị.</p>
            ) : lookup?.exists ? (
              <p className="rounded-lg bg-accent/10 px-3 py-2 text-sm text-ink sm:col-span-2">
                Số này đã có tài khoản ShopTrack.
              </p>
            ) : (
              <div className="space-y-1.5 sm:col-span-2">
                <span className="block text-sm text-inkSoft">Mật khẩu tạm *</span>
                <div className="flex gap-2">
                  <Input value={password} onChange={(e) => setPassword(e.target.value)} maxLength={100} className="h-11 font-mono desktop:h-9" disabled={save.isPending} autoComplete="off" />
                  <Button type="button" variant="outline" className="h-11 shrink-0 desktop:h-9" onClick={() => setPassword(makeTempPassword(browserRandom))} disabled={save.isPending}>Tạo mới</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {error && <p role="alert" className="text-sm font-medium text-danger">{error}</p>}

        <DialogFooter>
          {done ? (
            <Button type="button" onClick={() => onOpenChange(false)}>Đóng</Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>Hủy</Button>
              <Button type="button" onClick={submit} disabled={save.isPending || lookup?.member === true}>{save.isPending ? 'Đang thêm…' : 'Thêm nhân viên'}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
